import React, { useState, createContext, useContext, useCallback, Dispatch, SetStateAction, useMemo } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import MuiButton from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import InfoIcon from '@material-ui/icons/Info'
import useT from '../../../i18ns/admin/useT'
import { useCommonStyles, useDialogStyles } from '../../../utils/admin/StyleHook'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import { useGetDataByPayload, useReload, InitialFormFunc, useDialogHandleSubmit } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { AdminPlayerSetting, AdminPlayerSettingForm, PermissionType, FinanceRoleType, GameType, AdminPlayerSettingState } from '@golden/gdk-admin'
import { ValueGetter, FormValidation, createDefaultFormState, ChangedFormGetter } from '../../../utils/default/FormHook'
import { getValueFromCheckboxEvent, getValueFromValue } from '../../../utils/default/FormHelper'
import FormStateProvider from '../../default/form/FormStateProvider'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import FormField from '../../default/form/FormField'
import OnOffCheckbox, { PropTypes as OnOffCheckboxProps } from '../../default/form/OnOffCheckbox'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import MultipleSelector, { PropTypes as MultipleSelectorPropTypes } from '../../default/form/MultipleSelector'
import financeRoleTypeName from '../../../constants/admin/financeRoleTypeName'
import GameIdInput, { PropTypes as GameIdProps } from '../GameIdInput'
import { isUndefined, omitBy } from '@golden/utils'
import allRoute from '../route/route'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'

const useStyles = makeStyles((theme) => ({
  text: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  unhandledDeposit: {
    width: '1.5em',
    height: '1.5em',
    backgroundColor: '#eee',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  icon: {
    cursor: 'pointer',
    fontSize: 18
  }
}))

interface PropTypes {
  id: number
  section: string
  reloadFlag: boolean
  reload: () => void
  loaded: Dispatch<SetStateAction<string>>
}

interface FormType {
  fmWarned: boolean
  rcWarned: boolean
  isPasswordBlocked: boolean
  isFirstWithdrawLimit: boolean
  isRMBWithdrawLimit: boolean
  isSuspended: boolean
  notBetable: boolean
  notDepositable: boolean
  notWithdrawable: boolean
  notWithdrawableNumber: number
  notTransferable: boolean
  notActivity: boolean
  smsVerifyBlocked: boolean
  financeRolesDeposit: FinanceRoleType[]
  financeRolesWithdraw: FinanceRoleType[]
  forbiddenGames: GameType[]
}

const initialPlayerStates = {
  fm_warned: false,
  rc_warned: false,
  is_password_blocked: false,
  is_suspended: false,
  is_first_withdraw_limit: false,
  is_first_withdraw_limit_by_manual: false,
  is_rmb_withdraw_limit: false,
  not_betable: false,
  not_depositable: false,
  not_withdrawable: false,
  not_transferable: false,
  not_activity: false,
  not_sms_verify_sendable: false
}

const initialForm: InitialFormFunc<FormType> = (defaultForm) => ({
  fmWarned: false,
  rcWarned: false,
  isPasswordBlocked: false,
  isFirstWithdrawLimit: false,
  isRMBWithdrawLimit: false,
  isSuspended: false,
  notBetable: false,
  notDepositable: false,
  notWithdrawable: false,
  notWithdrawableNumber: 0,
  notTransferable: false,
  notActivity: false,
  smsVerifyBlocked: false,
  financeRolesDeposit: [],
  financeRolesWithdraw: [],
  forbiddenGames: [],
  ...defaultForm
})

const FormContext = createContext(createDefaultFormState(initialForm()))

const getValueFromEvent: ValueGetter<FormType> = {
  fmWarned: getValueFromCheckboxEvent,
  rcWarned: getValueFromCheckboxEvent,
  isPasswordBlocked: getValueFromCheckboxEvent,
  isFirstWithdrawLimit: getValueFromCheckboxEvent,
  isRMBWithdrawLimit: getValueFromCheckboxEvent,
  isSuspended: getValueFromCheckboxEvent,
  notBetable: getValueFromCheckboxEvent,
  notDepositable: getValueFromCheckboxEvent,
  notWithdrawable: getValueFromCheckboxEvent,
  notWithdrawableNumber: getValueFromValue,
  notTransferable: getValueFromCheckboxEvent,
  notActivity: getValueFromCheckboxEvent,
  smsVerifyBlocked: getValueFromCheckboxEvent,
  financeRolesDeposit: getValueFromValue,
  financeRolesWithdraw: getValueFromValue,
  forbiddenGames: getValueFromValue
}

const validation: FormValidation<FormType> = {
  fmWarned: [],
  rcWarned: [],
  isPasswordBlocked: [],
  isFirstWithdrawLimit: [],
  isRMBWithdrawLimit: [],
  isSuspended: [],
  notBetable: [],
  notDepositable: [],
  notWithdrawable: [],
  notWithdrawableNumber: [],
  notTransferable: [],
  notActivity: [],
  smsVerifyBlocked: [],
  financeRolesDeposit: [],
  financeRolesWithdraw: [],
  forbiddenGames: []
}

const Button = React.memo(MuiButton)

const allWithdrawFinanceRoles = Object.keys(financeRoleTypeName).map((key) => key as FinanceRoleType).filter((item) => (item.includes('withdraw')))

const PermissionStatus: React.FC = () => {
  const { value } = useContext(FormContext)
  const { t } = useT()

  const showStatusNormal = useMemo(() => {
    return (
      !value?.isPasswordBlocked &&
      !value?.isSuspended &&
      !value?.notBetable &&
      !value?.notDepositable &&
      !value?.notWithdrawable &&
      !value?.notTransferable &&
      !value?.notActivity &&
      !value?.smsVerifyBlocked &&
      !value?.financeRolesDeposit.length &&
      !value?.financeRolesWithdraw.length &&
      !value?.forbiddenGames.length &&
      !value?.isFirstWithdrawLimit &&
      !value?.isRMBWithdrawLimit
    )
  }, [value])

  const showStatusNotLoginable = useMemo(() => {
    return (value?.isSuspended || value?.isPasswordBlocked)
  }, [value])

  const showStatusNotDepositable = useMemo(() => {
    return (value?.isSuspended || value?.notDepositable || value?.financeRolesDeposit.length !== 0)
  }, [value])

  const showStatusNotDepositableNumber = useMemo(() => {
    return (!value?.isSuspended && !value?.notDepositable && value?.financeRolesDeposit.length !== 0)
  }, [value])

  const showStatusNotWithdrawable = useMemo(() => {
    return (value?.isSuspended || value?.notWithdrawable || value?.isFirstWithdrawLimit || value?.financeRolesWithdraw.length !== 0 || value?.isRMBWithdrawLimit)
  }, [value])

  const showStatusNotWithdrawableNumber = useMemo(() => {
    return (value.notWithdrawableNumber !== 0 && value.notWithdrawableNumber !== allWithdrawFinanceRoles.length)
  }, [value])

  const showStatusNotTransferable = useMemo(() => {
    return (value?.isSuspended || value?.notTransferable)
  }, [value])

  const showStatusNotActivity = useMemo(() => {
    return (value?.isSuspended || value?.notActivity)
  }, [value])

  const showStatusNotBetable = useMemo(() => {
    return (value?.isSuspended || value?.notBetable || value?.forbiddenGames.length !== 0)
  }, [value])

  const showStatusNotBetableNumber = useMemo(() => {
    return (!value?.isSuspended && !value?.notBetable && value?.forbiddenGames.length !== 0)
  }, [value])

  const showStatusSmsVerifyBlocked = useMemo(() => {
    return (value?.isSuspended || value?.smsVerifyBlocked)
  }, [value])

  return (
    <Box paddingY={1}>
      <Grid container direction="row" spacing={3}>
        {showStatusNormal && (
          <Grid item>
            <Typography>{t('userStatus.normal')}</Typography>
          </Grid>
        )}
        {showStatusNotLoginable && (
          <Grid item>
            <Typography color="error">{t('roleType.notLoginable')}</Typography>
          </Grid>
        )}
        {showStatusNotDepositable && (
          <Grid item>
            <Typography color="error">
              {t('roleType.notDepositable')}
              {showStatusNotDepositableNumber && (
                <span>{`(${value.financeRolesDeposit.length})`}</span>
              )}
            </Typography>
          </Grid>
        )}
        {showStatusNotWithdrawable && (
          <Grid item>
            <Typography color="error">
              {t('roleType.notWithdrawable')}
              {showStatusNotWithdrawableNumber && (
                <span>{`(${value.notWithdrawableNumber})`}</span>
              )}
            </Typography>
          </Grid>
        )}
        {showStatusNotTransferable && (
          <Grid item>
            <Typography color="error">{t('roleType.notTransferable')}</Typography>
          </Grid>
        )}
        {showStatusNotActivity && (
          <Grid item>
            <Typography color="error">{t('roleType.notActivity')}</Typography>
          </Grid>
        )}
        {showStatusNotBetable && (
          <Grid item>
            <Typography color="error">
              {t('roleType.notBetable')}
              {showStatusNotBetableNumber && (
                <span>{`(${value.forbiddenGames.length})`}</span>
              )}
            </Typography>
          </Grid>
        )}
        {showStatusSmsVerifyBlocked && (
          <Grid item>
            <Typography color="error">{t('roleType.smsVerifyBlocked')}</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
const PlayerStateCheckBoxes: React.FC = () => {
  const { value } = useContext(FormContext)
  const { t } = useT()
  const writable = useChecker([PermissionType.PLAYER_MANAGEMENT_PERMISSION])

  const financeRolesForbidOptions = useCallback((type: 'deposit' | 'withdraw') => {
    return Object.keys(financeRoleTypeName)
      .map((key) => key as FinanceRoleType)
      .map((key) => ({ name: t(financeRoleTypeName[key]), value: key }))
      .filter((item) => (item.value.includes(type)))
  }, [t])

  return (
    <Box marginBottom={4}>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12} md={4} lg={2}>
          <FormField<FormType, OnOffCheckboxProps>
            component={OnOffCheckbox}
            context={FormContext}
            name="isSuspended"
            label={t('common.freezeAccount')}
            disabled={!writable}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormField<FormType, OnOffCheckboxProps>
            component={OnOffCheckbox}
            value={value.isSuspended || value.notTransferable }
            context={FormContext}
            name="notTransferable"
            label={t('common.forbidTransfer')}
            disabled={!writable || value.isSuspended}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormField<FormType, OnOffCheckboxProps>
            component={OnOffCheckbox}
            value={value.isSuspended || value.notActivity }
            context={FormContext}
            name="notActivity"
            label={t('common.forbidActivity')}
            disabled={!writable || value.isSuspended }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField<FormType, GameIdProps>
            context={FormContext}
            component={GameIdInput}
            name="forbiddenGames"
            label={t('common.forbidBet')}
            fullWidth
            disabled={!writable || value.isSuspended }
            multiple
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField<FormType, MultipleSelectorPropTypes>
            context={FormContext}
            component={MultipleSelector}
            name="financeRolesDeposit"
            label={t('common.forbidDeposit')}
            options={financeRolesForbidOptions('deposit')}
            fullWidth
            disabled={!writable || value.isSuspended}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField<FormType, MultipleSelectorPropTypes>
            context={FormContext}
            component={MultipleSelector}
            name="financeRolesWithdraw"
            label={t('common.forbidWithdraw')}
            options={financeRolesForbidOptions('withdraw')}
            fullWidth
            disabled={!writable || value.isSuspended}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
const PlayerDetailPermissionSection: React.FC<PropTypes> = (props) => {
  const { id, section, reloadFlag: reloadFlagProps, reload: reloadProps, loaded } = props

  const gdk = useGDK()
  const writable = useChecker([PermissionType.PLAYER_MANAGEMENT_PERMISSION])
  const globalDialog = useGlobalDialog()
  const { reload, reloadFlag } = useReload()
  const pageFlow = usePageFlow()
  const { t } = useT()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const dialogClasses = useDialogStyles()

  const [playerStates, setPlayerStates] = useState<AdminPlayerSettingState>(initialPlayerStates)

  const [defaultForm, setDefaultForm] = useState<FormType>(initialForm())
  const [depositOrderNo, setDepositOrderNo] = useState<{ type: null | 'golden' | 'third_party', value: null | string }>({
    type: null,
    value: null
  })

  const openIsFirstWalletLimitInfo = useCallback((mode: 'firstWithdrawLimit' | 'rmbWithdrawLimit') => {
    const isFirstWithdrawLimitModeMessage: React.ReactElement = (
      <Box textAlign='left'>
        <Box marginBottom={1}>
          <Typography variant="h6">{t(`dialog.${mode}.1.title`)}</Typography>
          <Typography>{t(`dialog.${mode}.1.content.1`)}</Typography>
          <Typography>{t(`dialog.${mode}.1.content.2`)}</Typography>
          <Typography>{t(`dialog.${mode}.1.content.3`)}</Typography>
        </Box>
        <Box marginBottom={1}>
          <Typography variant="h6">{t(`dialog.${mode}.2.title`)}</Typography>
          <Typography>{t(`dialog.${mode}.2.content.1`)}</Typography>
          <Typography>{t(`dialog.${mode}.2.content.2`)}</Typography>
        </Box>
        { (mode === 'firstWithdrawLimit') && (
          <Box>
            <Typography variant="h6">{t('dialog.firstWithdrawLimit.3.title')}</Typography>
            <Typography>{t('dialog.firstWithdrawLimit.3.content')}</Typography>
          </Box>
        )}
      </Box>
    )
    globalDialog.setConfig(createGlobalDialogConfig({
      showCancel: false,
      showIcon: false,
      message: isFirstWithdrawLimitModeMessage
    }))
    globalDialog.setOpen({
      id: 'is-first=wallet-limit-info',
      value: true,
      isOK: false
    })
  }, [globalDialog, t])

  const allFinanceRoles = Object.keys(financeRoleTypeName).map((key) => key as FinanceRoleType)

  const getNotWithdrawableNumber = useCallback((form: FormType) => {
    const withdrawChecker = allWithdrawFinanceRoles
      .map(((role) => {
        let shouldDisabled = false
        if (form.financeRolesWithdraw.includes(role)) {
          shouldDisabled = true
        }
        if (form.isFirstWithdrawLimit && role !== FinanceRoleType.NOT_WITHDRAW_DEBIT_CARD) {
          // 「下點需用銀行卡」將禁用銀行卡以外所有下點方法
          shouldDisabled = true
        }
        if (form.isRMBWithdrawLimit && role === FinanceRoleType.NOT_WITHDRAW_ALIPAY) {
          // 「人民幣下點需用銀行卡」將禁用非銀行卡的人民幣方法（目前僅有支付寶）
          shouldDisabled = true
        }
        return {
          role,
          disabled: shouldDisabled
        }
      }))
      .filter((el) => (el.disabled))
    return withdrawChecker.length
  }, [allWithdrawFinanceRoles])

  const getChangedForm: ChangedFormGetter<FormType> = {
    financeRolesDeposit: (value, form) => (
      {
        ...form,
        financeRolesDeposit: value,
        notDepositable: (value.length === allFinanceRoles.filter((el) => (el.includes('deposit'))).length)
      }),
    financeRolesWithdraw: (value, form) => (
      {
        ...form,
        financeRolesWithdraw: value,
        notWithdrawable: (value.length === allFinanceRoles.filter((el) => (el.includes('withdraw'))).length),
        notWithdrawableNumber: (getNotWithdrawableNumber({ ...form, financeRolesWithdraw: value }))
      }),
    forbiddenGames: (value, form) => (
      {
        ...form,
        forbiddenGames: value,
        notBetable: (value.length === aliveGames.length)
      }),
    isFirstWithdrawLimit: (value, form) => (
      {
        ...form,
        isFirstWithdrawLimit: value,
        notWithdrawableNumber: (getNotWithdrawableNumber({ ...form, isFirstWithdrawLimit: value }))
      }),
    isRMBWithdrawLimit: (value, form) => (
      {
        ...form,
        isRMBWithdrawLimit: value,
        notWithdrawableNumber: (getNotWithdrawableNumber({ ...form, isRMBWithdrawLimit: value }))
      })
  }

  const formToRequest = (form: FormType): AdminPlayerSettingForm => {
    const converted = {
      fm_warned: form.fmWarned,
      rc_warned: form.rcWarned,
      is_password_blocked: form.isPasswordBlocked,
      is_suspended: form.isSuspended,
      is_first_withdraw_limit_by_manual: !playerStates.is_first_withdraw_limit && form.isFirstWithdrawLimit,
      is_first_withdraw_limit: playerStates.is_first_withdraw_limit && form.isFirstWithdrawLimit,
      is_rmb_withdraw_limit: form.isRMBWithdrawLimit,
      not_betable: form.notBetable,
      not_depositable: form.notDepositable,
      not_withdrawable: form.notWithdrawable,
      not_transferable: form.notTransferable,
      not_activity: form.notActivity,
      not_sms_verify_sendable: form.smsVerifyBlocked,
      finance_roles: form.financeRolesDeposit.concat(form.financeRolesWithdraw),
      forbidden_games: form.forbiddenGames
    } as AdminPlayerSettingForm
    return omitBy(converted, isUndefined) as AdminPlayerSettingForm
  }

  const aliveGames = useGDKStore.platform.aliveGames()

  useGetDataByPayload({
    payload: id,
    gdkFunc: (payload) => gdk.player.getPlayerSetting(payload),
    gdkFuncDependencies: [gdk, reloadFlag, reloadFlagProps],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: AdminPlayerSetting) => {
      setDepositOrderNo({
        type: res.deposit_forwarder_type,
        value: res.deposit_order_number
      })
      setPlayerStates(res.state)
      setDefaultForm(initialForm({
        fmWarned: res.state.fm_warned,
        rcWarned: res.state.rc_warned,
        isPasswordBlocked: res.state.is_password_blocked,
        isFirstWithdrawLimit: res.state.is_first_withdraw_limit || res.state.is_first_withdraw_limit_by_manual,
        isRMBWithdrawLimit: res.state.is_rmb_withdraw_limit,
        isSuspended: res.state.is_suspended,
        notBetable: (res.state.not_betable),
        notDepositable: (res.state.not_depositable),
        notWithdrawable: (res.state.not_withdrawable),
        notWithdrawableNumber: (getNotWithdrawableNumber({
          ...defaultForm,
          isFirstWithdrawLimit: res.state.is_first_withdraw_limit || res.state.is_first_withdraw_limit_by_manual,
          isRMBWithdrawLimit: res.state.is_rmb_withdraw_limit,
          financeRolesWithdraw: (res.state.not_withdrawable) ? allFinanceRoles.filter((el) => (el.includes('withdraw'))) : res.finance_roles.filter((el) => (el.includes('withdraw')))
        })),
        notTransferable: (res.state.not_transferable),
        notActivity: (res.state.not_activity),
        smsVerifyBlocked: (res.state.not_sms_verify_sendable),
        financeRolesDeposit: (res.state.not_depositable) ? allFinanceRoles.filter((el) => (el.includes('deposit'))) : res.finance_roles.filter((el) => (el.includes('deposit'))),
        financeRolesWithdraw: (res.state.not_withdrawable) ? allFinanceRoles.filter((el) => (el.includes('withdraw'))) : res.finance_roles.filter((el) => (el.includes('withdraw'))),
        forbiddenGames: (res.state.not_betable) ? aliveGames.map((game) => (game.id)) : (res.forbidden_games)
      }))
      pageFlow.setContentShow()
      loaded(section)
    },
    onSuccessDependencies: [aliveGames],
    onError: pageFlow.setGDKError
  })
  const { handleSubmit } = useDialogHandleSubmit({
    formToRequest,
    globalDialog,
    dialogId: 'updatePlayerSetting',
    gdkFunc: (payload) => gdk.player.updatePlayerSetting(id, payload),
    gdkFuncDependencies: [gdk, id],
    getChangeDialogConfig: (form) => createGlobalDialogConfig({
      showIcon: false,
      notUseTypo: true,
      message: (
        <React.Fragment>
          <Typography className={dialogClasses.text}>{t('dialog.confirmPlayerSetting')}</Typography>
          {
            (form.fmWarned ||
            form.rcWarned ||
            form.isSuspended ||
            form.isFirstWithdrawLimit ||
            form.isRMBWithdrawLimit ||
            form.isPasswordBlocked ||
            form.notBetable ||
            form.notDepositable ||
            form.notWithdrawable ||
            form.notTransferable ||
            form.notActivity ||
            form.smsVerifyBlocked ||
            (form.financeRolesDeposit.length !== 0) ||
            (form.financeRolesWithdraw.length !== 0) ||
            (form.forbiddenGames.length !== 0)) && (
              <Typography className={clsx(dialogClasses.text, commonClasses.purpleWord)}>
                [{form.fmWarned && (<span className={classes.text}>{`${t('common.finance')} ${t('common.warning')}`}</span>)}
                {form.rcWarned && (<span className={classes.text}>{`${t('common.riskControl')} ${t('common.warning')}`}</span>)}
                {form.isSuspended && (<span className={classes.text}>{t('common.freezeAccount')}</span>)}
                {form.isFirstWithdrawLimit && (<span className={classes.text}>{t('common.firstWithdrawLimit')}</span>)}
                {form.isRMBWithdrawLimit && (<span className={classes.text}>{t('common.rmbWithdrawLimit')}</span>)}
                {form.isPasswordBlocked && (<span className={classes.text}>{t('common.freezeAccountPwdWrong')}</span>)}
                {(form.notBetable || form.forbiddenGames.length !== 0) && (<span className={classes.text}>{t('common.forbidBet')}</span>)}
                {(!form.notBetable && form.forbiddenGames.length !== 0) && (<span className={classes.text}>{`(${form.forbiddenGames.length})`}</span>)}
                {(form.notDepositable || (form.financeRolesDeposit.length !== 0)) && (<span className={classes.text}>{t('common.forbidDeposit')}</span>)}
                {(!form.notDepositable && form.financeRolesDeposit.length !== 0) && (<span className={classes.text}>{`(${form.financeRolesDeposit.length})`}</span>)}
                {(form.isFirstWithdrawLimit || form.isRMBWithdrawLimit || form.notWithdrawable || (form.financeRolesWithdraw.length !== 0)) && (<span className={classes.text}>{t('common.forbidWithdraw')}</span>)}
                {(form.notWithdrawableNumber !== 0 && form.notWithdrawableNumber !== allWithdrawFinanceRoles.length) && (<span className={classes.text}>{`(${form.notWithdrawableNumber})`}</span>)}
                {form.notTransferable && (<span className={classes.text}>{t('common.forbidTransfer')}</span>)}
                {form.notActivity && (<span className={classes.text}>{t('common.forbidActivity')}</span>)}
                {form.smsVerifyBlocked && (<span className={classes.text}>{t('common.sms')}</span>)}]
              </Typography>
            )
          }
        </React.Fragment>
      )
    }),
    getSuccessDialogConfig: () => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.playerSettingSuccess')
    }),
    getFailDialogConfig: (error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }),
    onSuccess: () => {
      reload()
      reloadProps()
    }
  })
  return (
    <div id={section}>
      <FormStateProvider
        context={FormContext}
        defaultValue={defaultForm}
        onSubmit={handleSubmit}
        getValueFromEvent={getValueFromEvent}
        getChangedForm={getChangedForm}
        validation={validation}
      >
        <Paper>
          <Box padding={4}>
            <Box
              paddingY={1.25}
              paddingX={2}
              className={commonClasses.pinkTitleBar}
            >
              <Typography variant="h5">
                {t('common.playerPermissionSetting')}
              </Typography>
            </Box>
            <Box paddingY={2}>
              <LoadingAndErrorFrame { ...pageFlow.status }>
                <Typography variant="h5">
                  {t('common.status')}
                </Typography>
                <PermissionStatus />
                <Box paddingY={2}>
                  <Typography variant="h5">
                    {t('common.setting')}
                  </Typography>
                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} md={4} lg={3}>
                      <FormField<FormType, OnOffCheckboxProps>
                        component={OnOffCheckbox}
                        context={FormContext}
                        name="fmWarned"
                        label={`${t('common.finance')} ${t('common.warning')}`}
                        disabled={!writable}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <FormField<FormType, OnOffCheckboxProps>
                        component={OnOffCheckbox}
                        context={FormContext}
                        name="rcWarned"
                        label={`${t('common.riskControl')} ${t('common.warning')}`}
                        disabled={!writable}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <FormField<FormType, OnOffCheckboxProps>
                        component={OnOffCheckbox}
                        context={FormContext}
                        name="isPasswordBlocked"
                        label={t('common.freezeAccountPwdWrong')}
                        disabled={!writable || !playerStates.is_password_blocked}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <FormField<FormType, OnOffCheckboxProps>
                        component={OnOffCheckbox}
                        context={FormContext}
                        name="smsVerifyBlocked"
                        label={t('common.smsVerifyBlockedCheckbox')}
                        disabled={!writable || !playerStates.not_sms_verify_sendable}
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={4} lg={3}>
                      <Box display='flex' alignItems='center'>
                        <Box>
                          <FormField<FormType, OnOffCheckboxProps>
                            component={OnOffCheckbox}
                            context={FormContext}
                            name="isFirstWithdrawLimit"
                            label={t('common.firstWithdrawLimit')}
                            disabled={!writable}
                          />
                        </Box>
                        <Box>
                          <InfoIcon
                            className={classes.icon}
                            onClick={() => { openIsFirstWalletLimitInfo('firstWithdrawLimit') }}
                          />
                        </Box>
                        {
                          depositOrderNo.value && (
                            <Box marginLeft={1}>
                              <Link
                                className={commonClasses.anchor}
                                to={allRoute[depositOrderNo.type === 'third_party' ? 'depositThirdPartySlip' : 'depositSlip'].encodePath({ param: {}, search: { order_number: depositOrderNo.value, page: 1 } })}
                                target="_blank"
                              >
                                {t('common.depositOrder')}
                              </Link>
                            </Box>
                          )
                        }
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Box display='flex' alignItems='center'>
                        <Box>
                          <FormField<FormType, OnOffCheckboxProps>
                            component={OnOffCheckbox}
                            context={FormContext}
                            name="isRMBWithdrawLimit"
                            label={t('common.rmbWithdrawLimit')}
                            disabled={!writable}
                          />
                        </Box>
                        <Box>
                          <InfoIcon
                            className={classes.icon}
                            onClick={() => { openIsFirstWalletLimitInfo('rmbWithdrawLimit') }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <PlayerStateCheckBoxes />
                </Box>
                {writable && (
                  <Box display="flex" justifyContent="flex-end">
                    <FormSubmitButton
                      context={FormContext}
                      component={Button}
                      className={commonClasses.purpleGradualButton}
                      type="submit"
                    >
                      {t('common.saveSetting')}
                    </FormSubmitButton>
                  </Box>
                )}
              </LoadingAndErrorFrame>
            </Box>
          </Box>
        </Paper>
      </FormStateProvider>
    </div>
  )
}

export default React.memo(PlayerDetailPermissionSection)
